/**
 * Servicio de manifiestos: una consulta por estado, agrupación en cliente
 */
import axiosInstance from '../../api/axiosInstance'
import { MANIFIESTO_SOURCE_STATUS } from '../../types/manifiestos'

interface ListResponse {
  success?: boolean
  message?: string
  data?: unknown[]
  meta?: {
    totalItems?: number
    totalPages?: number
    currentPage?: number
  }
}

const PAGE_SIZE = 100

/**
 * Trae todos los pedidos en estado listo para despachar (todas las transportadoras).
 */
export async function getManifiestoOrdersByStatus(): Promise<unknown[]> {
  const params = new URLSearchParams()
  params.set('page', '1')
  params.set('limit', String(PAGE_SIZE))
  params.set('states', MANIFIESTO_SOURCE_STATUS)

  const firstResponse = await axiosInstance.get<ListResponse>(
    `/list?${params.toString()}`
  )

  if (firstResponse.data?.success === false) {
    throw new Error(
      firstResponse.data.message ||
        'Error al obtener pedidos listos para despachar'
    )
  }

  const totalItems = Number(firstResponse.data?.meta?.totalItems ?? 0)
  let allOrders = [...(firstResponse.data?.data || [])]

  if (totalItems <= PAGE_SIZE) {
    return allOrders
  }

  const totalPages = Math.ceil(totalItems / PAGE_SIZE)
  for (let page = 2; page <= totalPages; page += 1) {
    params.set('page', String(page))
    const pageResponse = await axiosInstance.get<ListResponse>(
      `/list?${params.toString()}`
    )
    if (pageResponse.data?.success === false) {
      throw new Error(
        pageResponse.data.message ||
          `Error al obtener página ${page} de pedidos listos`
      )
    }
    allOrders = [...allOrders, ...(pageResponse.data?.data || [])]
  }

  return allOrders
}

/**
 * Completa cada pedido con GET /list/:id (dirección, lineItems, peso, etc.)
 */
export async function enrichOrdersWithDetail(
  orders: unknown[]
): Promise<unknown[]> {
  const enriched: unknown[] = []

  for (const raw of orders) {
    const order = raw as {
      id?: number
      shippingAddress?: unknown
      shipping?: unknown
      products?: unknown
      lineItems?: unknown
      line_items?: unknown
    }

    const hasShipping =
      order.shippingAddress != null ||
      order.shipping != null ||
      Boolean((order as { shipping_address_1?: string }).shipping_address_1)
    const hasProducts =
      (Array.isArray(order.products) && order.products.length > 0) ||
      (Array.isArray(order.lineItems) && order.lineItems.length > 0) ||
      (Array.isArray(order.line_items) && order.line_items.length > 0)

    if (hasShipping && hasProducts) {
      enriched.push(raw)
      continue
    }

    const orderId = order.id
    if (orderId == null || typeof orderId !== 'number') {
      enriched.push(raw)
      continue
    }

    try {
      const detailResponse = await axiosInstance.get(`/list/${orderId}`)
      const detail =
        (detailResponse.data as { data?: unknown })?.data ??
        detailResponse.data
      enriched.push(
        detail && typeof detail === 'object'
          ? { ...(raw as object), ...(detail as object) }
          : raw
      )
    } catch {
      enriched.push(raw)
    }
  }

  return enriched
}
